"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback } from "react";
import { useFullstory } from "@/hooks/useFullstory";
import { trackElement as buildTrackingAttrs } from "@/lib/tracking/analytics";
import { useFeatureTrackingProps } from "@/lib/tracking/FeatureTrackingContext";
export const EVENTS = {
  AI_FEEDBACK_BUTTON_CLICKED: "AI Feedback Button Clicked",
  AI_FEEDBACK_SUBMITTED: "AI Feedback Submitted",
  ANNOTATION_CREATED: "Annotation Created",
  ANNOTATION_DELETED: "Annotation Deleted",
  CHANGE_ORDER_ARCHIVED: "Change Order Archived",
  CHANGE_ORDER_COMPLETED: "Change Order Completed",
  CHANGE_ORDER_CONFIRMED: "Change Order Confirmed",
  CHANGE_ORDER_DENIED: "Change Order Denied",
  CHANGE_ORDER_REOPENED: "Change Order Reopened",
  CHANGE_ORDER_UNARCHIVED: "Change Order Unarchived",
  CHAT_QUERY: "Chat Query",
  COMPARISON_CHANGE_VIEWED: "Comparison Change Viewed",
  CONTACT_SALES_SUBMITTED: "Contact Sales Submitted",
  DOCUMENT_DOWNLOADED: "Crunch Downloaded",
  DROPDOWN_ITEM_SELECTED: "Dropdown Item Selected",
  EMAIL_SETTINGS_LINK_CLICKED: "Email Settings Link Clicked",
  INTEGRATION_IMPORT_COMPLETED: "Integration Import Completed",
  INTEGRATION_IMPORT_STARTED: "Integration Import Started",
  MOVE_DESTINATION_TEAM_CHANGED: "Move Destination Team Changed",
  NOTIFICATION_PREFERENCE_CHANGED: "Notification Preference Changed",
  NOTIFICATIONS_MASTER_TOGGLED: "Notifications Master Toggled",
  TRIAL_ONBOARDING_ANSWER_SUBMITTED: "Trial Onboarding Answer Submitted",
  UPLOAD_DESTINATION_CHANGED: "Upload Destination Changed",
  ONBOARDING_COMPLETED: "Onboarding Completed",
  ONBOARDING_SHOWN: "Onboarding Shown",
  ONBOARDING_SKIPPED: "Onboarding Skipped",
  ONBOARDING_STEP_NEXT: "Onboarding Step Next",
  ONBOARDING_STEP_VIEWED: "Onboarding Step Viewed",
  ONBOARDING_TASKS_ALL_VIEWED: "Onboarding Tasks All Viewed",
  ONBOARDING_TASKS_CLOSED: "Onboarding Tasks Closed",
  ONBOARDING_TASKS_GOAL_VIEWED: "Onboarding Tasks Goal Viewed",
  ONBOARDING_TASKS_OPENED: "Onboarding Tasks Opened",
  ONBOARDING_TASKS_TRY_IT: "Onboarding Tasks Try It",
  REVIEW_ITEM_VIEWED: "Review Item Viewed",
  REVIEW_ITEM_COMPLETED: "Review Item Completed",
  REVIEW_ITEM_COMMENTED: "Review Item Commented",
  USER_MENTIONED: "User Mentioned",
  TASK_COMPLETED: "Task Completed",
  TASK_CREATED: "Task Created",
  TASK_DELETED: "Task Deleted",
  TASK_UPDATED: "Task Updated",
  FILTERS_RESET: "Reset Filters",
  SEARCH_QUERY: "Search Query",
  PANEL_OPENED: "Panel Opened",
  PROJECT_CREATED: "Project Created",
  PROJECT_CREATE_ABANDONED: "Project Create Abandoned",
  PROJECT_CREATE_MODAL_OPENED: "Project Create Modal Opened",
  PROJECT_FIELD_SETTINGS_SAVED: "Project Field Settings Saved",
  PROJECT_FIELD_SETTINGS_VIEWED: "Project Field Settings Viewed",
  PROJECT_BULK_UPDATED: "Project Bulk Updated",
  SAMPLE_PROJECT_EXPLORE_CLICKED: "Sample Project Explore Clicked",
  SAMPLE_PROJECT_RETURN_CLICKED: "Sample Project Return Clicked",
  PUBLIC_TRIAL_REGISTERED: "Public Trial Registered",
  PUBLIC_TRIAL_REGISTRATION_FAILED: "Public Trial Registration Failed",
  SOURCE_LINK_CLICKED: "Source Link Clicked",
  MANUAL_SKILL_INVOKED: "Manual Skill Invoked",
  MEMORY_ENABLED: "Memory Enabled",
  MEMORY_DISABLED: "Memory Disabled",
  MEMORY_SAVED: "Memory Saved",
  MEMORY_OPTIMIZATION_REQUESTED: "Memory Optimization Requested",
  MEMORY_OPTIMIZATION_RESOLVED: "Memory Optimization Resolved",
  STEP_REASSIGNED: "Step Reassigned",
  TEXT_COPIED: "Text Copied",
  THREAD_VIEWED_BY_NON_OWNER: "Thread Viewed By Non-Owner",
  WORKFLOW_STARTED: "Workflow Started"
} as const;
type EventName = (typeof EVENTS)[keyof typeof EVENTS];

/**
 * Hook for event and page tracking with optional shared common properties.
 * Common properties are merged into every trackEvent call; call-site properties take precedence.
 * trackElement wraps buildTrackingAttrs with auto-merged FeatureTrackingContext properties.
 */
export function useAnalytics(commonProperties) {
  const $ = _c(14);
  const {
    trackEvent: fsTrackEvent,
    setPageProperties
  } = useFullstory();
  const contextProps = useFeatureTrackingProps();
  let t0;
  if ($[0] !== commonProperties || $[1] !== contextProps || $[2] !== fsTrackEvent) {
    t0 = (name, properties) => {
      fsTrackEvent(name, {
        ...contextProps,
        ...commonProperties,
        ...properties
      });
    };
    $[0] = commonProperties;
    $[1] = contextProps;
    $[2] = fsTrackEvent;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const trackEvent = t0;
  let t1;
  if ($[4] !== contextProps) {
    t1 = (type, name_0, properties_0) => buildTrackingAttrs(type, name_0, {
      ...contextProps,
      ...properties_0
    });
    $[4] = contextProps;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const trackElement = t1;
  let t2;
  if ($[6] !== commonProperties || $[7] !== contextProps || $[8] !== setPageProperties) {
    t2 = (pageName, properties_1) => {
      setPageProperties(pageName, {
        ...contextProps,
        ...commonProperties,
        ...properties_1
      });
    };
    $[6] = commonProperties;
    $[7] = contextProps;
    $[8] = setPageProperties;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const wrappedSetPageProperties = t2;
  let t3;
  if ($[10] !== trackElement || $[11] !== trackEvent || $[12] !== wrappedSetPageProperties) {
    t3 = {
      trackEvent,
      setPageProperties: wrappedSetPageProperties,
      trackElement
    };
    $[10] = trackElement;
    $[11] = trackEvent;
    $[12] = wrappedSetPageProperties;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
}